import type {
  Pan,
  Zoom,
  DiagramNodeData,
  DiagramLinkData,
  DiagramLayoutOutput,
  ItemInfo,
  Bounds,
  Position,
  Dimensions,
  DatatipContext,
  DiagramLabelStyles,
  DiagramLabelPosition
} from '../diagram.types';
import type { Ref } from 'preact/hooks';
import type { BundleType } from '../../resources/nls/bundle';
import type {
  Font,
  GetTextDimensionsType,
  TextDimensions
} from '../../hooks/PRIVATE_useTextDimensions';
import { ComponentChildren } from 'preact';
import { Property } from 'csstype';
import { generateAriaLabel } from '#utils/UNSAFE_visUtils/accUtils';

const ZOOM_TO_FIT_PADDING = 20;
/**
 * Returns the pan properties for the diagram.
 */
export function getPanProps(props: Pan, contentBounds: Bounds) {
  return {
    panning: props.panning ? props.panning : 'off',
    panDirection: props.panDirection ? props.panDirection : 'any',
    centerX: props.centerX !== undefined ? props.centerX : contentBounds.w / 2 + contentBounds.x,
    centerY: props.centerY !== undefined ? props.centerY : contentBounds.h / 2 + contentBounds.y,
    onPan: props.onPan
  };
}

/**
 * Returns the zoom properties for the diagram.
 */
export function getZoomProps(props: Zoom, width: number, height: number, contentBounds: Bounds) {
  const minZoom = props.minZoom ? props.minZoom : getZoomToFitZoom(width, height, contentBounds);
  const maxZoom = props.maxZoom !== undefined ? props.maxZoom : 1;
  const zoom = props.zoomValue ? props.zoomValue : getZoomToFitZoom(width, height, contentBounds);
  return {
    zooming: props.zooming ? props.zooming : 'off',
    // default value of 0 indicates zoom-to-fit level
    minZoom: minZoom,
    maxZoom: maxZoom,
    // zoom value of 0 will default to zoom to fit
    zoomValue: constrainZoom(zoom, minZoom, maxZoom),
    onZoom: props.onZoom
  };
}

/**
 * Constructs the json object to be passed into the layout function
 */
export function constructLayoutJSON<
  K1,
  K2,
  D1 extends DiagramNodeData<K1>,
  D2 extends DiagramLinkData<K2, K1>
>(
  nodes: D1[],
  nodeItems: Map<K1, D1>,
  linkItems: Map<K2, D2>,
  dimensions: Dimensions[],
  width: number,
  height: number,
  getTextDimensions?: GetTextDimensionsType
) {
  const labelDims: Map<K1 | K2, TextDimensions> = new Map();
  const nodeKeys = Array.from(nodeItems.keys());
  for (const id of nodeKeys) {
    const item = nodeItems.get(id as K1);
    if (item && getTextDimensions && item.label) {
      const label = item.label;
      const fontProps = _getFontProps(item.labelStyle);
      labelDims.set(id as K1 | K2, getTextDimensions(label, fontProps));
    }
  }
  const linkKeys = Array.from(linkItems.keys());
  for (const id of linkKeys) {
    const item = linkItems.get(id as K2);
    if (item && getTextDimensions && item.label) {
      const label = item.label;
      const fontProps = _getFontProps(item.labelStyle);
      labelDims.set(id as K1 | K2, getTextDimensions(label, fontProps));
    }
  }
  const getLabelDimensions = function (data: D1 | D2) {
    return labelDims.get(data.id);
  };
  const getNodeDimensions = function (data: D1) {
    for (let i = 0; i < nodes.length; i++) {
      if (data.id === nodes[i].id) {
        return dimensions[i];
      }
    }
    return dimensions[0];
  };

  return {
    layoutJSON: {
      getNodeDimensions: getNodeDimensions,
      getLabelDimensions: getLabelDimensions,
      componentSize: {
        width: width,
        height: height
      }
    },
    labelDims: labelDims
  };
}

/**
 * Deconstructs the output of the layout function
 */
export function deconstructLayoutJSON<
  K1,
  K2,
  D1 extends DiagramNodeData<K1>,
  D2 extends DiagramLinkData<K2, K1>
>(layoutJSON: DiagramLayoutOutput, nodeData: D1[], linkData: D2[]) {
  const nodes = layoutJSON.nodes;
  const links = layoutJSON.links;
  const positions: Position[] = [];
  const nodeLabelPos: (DiagramLabelPosition | undefined)[] = [];
  const linkLabelPos: DiagramLabelPosition[] = [];
  let hasNodeLabels = false;
  let hasLinkLabels = false;
  let node, link;
  for (let i = 0; i < nodeData.length; i++) {
    node = nodes[nodeData[i].id as string | number];
    positions.push(node.position);
    if (node.label) hasNodeLabels = true;
    nodeLabelPos.push(node.label);
  }
  if (links) {
    for (let i = 0; i < linkData.length; i++) {
      link = links[linkData[i].id as string | number];
      if (link.label) hasLinkLabels = true;
      linkLabelPos.push(link.label);
    }
  }
  return {
    nodePoints: positions,
    nodeLabelPos: hasNodeLabels ? nodeLabelPos : undefined,
    linkLabelPos: hasLinkLabels ? linkLabelPos : undefined
  };
}

/**
 * Merges the node points into the dimensions
 */
export function mergeDimensions(dimensions: Dimensions[], nodePoints: Position[]) {
  const newBounds: Bounds[] = [];
  for (let i = 0; i < dimensions.length; i++) {
    const point = nodePoints[i];
    const dim = dimensions[i];
    newBounds.push({ x: point.x, y: point.y, w: dim.w, h: dim.h });
  }
  return newBounds;
}

/**
 * Returns the text for the diagram datatip.
 */
export function getDatatipContent<
  K1,
  K2,
  D1 extends DiagramNodeData<K1>,
  D2 extends DiagramLinkData<K2, K1>
>(
  nodesMap: Map<K1, D1>,
  linksMap: Map<K2, D2>,
  focusedItem?: ItemInfo<K1, K2>,
  hoveredItem?: ItemInfo<K1, K2>,
  datatip?: (detail: DatatipContext<D1, D2>) => {
    content: ComponentChildren;
    borderColor?: Property.BorderColor;
  }
) {
  let itemInfo;
  const isPointerActive = hoveredItem?.isCurrent;
  if (isPointerActive && hoveredItem.id != null) {
    itemInfo = hoveredItem;
  }
  if (focusedItem && focusedItem.isCurrent) {
    itemInfo = focusedItem;
  }
  if (itemInfo && datatip) {
    return datatip(
      itemInfo.isNode
        ? { data: nodesMap.get(itemInfo.id as K1) as D1, type: 'node' }
        : { data: linksMap.get(itemInfo.id as K2) as D2, type: 'link' }
    );
  }
  if (itemInfo) {
    const text = (
      itemInfo.isNode ? nodesMap.get(itemInfo.id as K1) : linksMap.get(itemInfo.id as K2)
    )?.accessibleLabel;
    return {
      content: text
    };
  }
  return;
}

/**
 * Creates a map with key id and value node or link data
 */
export function createItemMap<
  K1,
  K2,
  D1 extends DiagramNodeData<K1>,
  D2 extends DiagramLinkData<K2, K1>
>(items: (D1 | D2)[]) {
  const itemIdToDataMap = new Map();
  const itemIdToIndexMap = new Map();
  const itemIndexToIdMap = new Map();
  for (let i = 0; i < items.length; i++) {
    itemIdToDataMap.set(items[i].id, items[i]);
    itemIdToIndexMap.set(items[i].id, i);
    itemIndexToIdMap.set(i, items[i].id);
  }
  return {
    itemIdToDataMap,
    itemIdToIndexMap,
    itemIndexToIdMap
  };
}

/**
 * Filter out links with no start or end nodes
 */
export function filterLinksWithNoEndNodes<K2, K1, D1, D2 extends DiagramLinkData<K2, K1>>(
  links: D2[],
  nodesMap: Map<K1, D1>
) {
  const newLinks: D2[] = [];
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    if (nodesMap.get(link.startNode) && nodesMap.get(link.endNode)) {
      newLinks.push(link);
    }
  }
  return newLinks;
}

/**
 * Pulls out the dimensions from an array of bounds
 */
export function getDimensionsFromBounds(bounds: Bounds[]) {
  const dims: Dimensions[] = [];
  for (let i = 0; i < bounds.length; i++) {
    dims.push({ w: bounds[i].w, h: bounds[i].h });
  }
  return dims;
}

/**
 * Returns true if event is a selection event
 */
export function isSelectionEvent(event: KeyboardEvent): boolean {
  if (event.altKey && (event.key === '≥' || event.key === '≤')) {
    return true;
  }
  return false;
}

/**
 * Returns the center position of the node or link
 * For links, draws a straight line between the centers of the start/end node
 * and returns the center of that line
 */
export function getFocusedItemBounds<K1, K2, D2 extends DiagramLinkData<K2, K1>>(
  focusedItemInfo: ItemInfo<K1, K2>,
  bounds: Bounds[],
  links: D2[],
  nodeIdToIndexMap: Map<K1, number>,
  linkIdToIndexMap: Map<K2, number>
) {
  if (focusedItemInfo.isNode) {
    const idx = nodeIdToIndexMap.get(focusedItemInfo.id as K1);
    // Return the first element if cannot find the Node index
    return bounds[idx || 0];
  } else {
    // Center of a straight line between start/end node centers
    const link = links[linkIdToIndexMap.get(focusedItemInfo.id as K2) || 0];
    const startNB = bounds[nodeIdToIndexMap.get(link.startNode) || 0];
    const endNB = bounds[nodeIdToIndexMap.get(link.endNode) || 0];
    const startNCenter = { x: startNB.x + startNB.w / 2, y: startNB.y + startNB.h / 2 };
    const endNCenter = { x: endNB.x + endNB.w / 2, y: endNB.y + endNB.h / 2 };
    const minX = Math.min(startNCenter.x, endNCenter.x);
    const minY = Math.min(startNCenter.y, endNCenter.y);
    return {
      x: minX,
      y: minY,
      w: Math.abs(startNCenter.x - endNCenter.x),
      h: Math.abs(startNCenter.y - endNCenter.y)
    };
  }
}
/**
 * Combines the node and link ids into one array
 */
export function combineIds<K1, K2>(nodeIds?: K1[], linkIds?: K2[]) {
  const combinedIds: (K1 | K2)[] = [];
  if (nodeIds) {
    for (let i = 0; i < nodeIds.length; i++) {
      combinedIds.push(nodeIds[i]);
    }
  }
  if (linkIds) {
    for (let i = 0; i < linkIds.length; i++) {
      combinedIds.push(linkIds[i]);
    }
  }
  return combinedIds;
}

/**
 * Returns the bounds of the diagram content
 */
export function getContentBounds(nodeBounds: Bounds[], labelBounds?: Bounds): Bounds {
  if (nodeBounds.length === 0) return { x: 0, w: Infinity, y: 0, h: Infinity };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let dims;
  for (let i = 0; i < nodeBounds.length; i++) {
    dims = nodeBounds[i];
    minX = dims.x < minX ? dims.x : minX;
    minY = dims.y < minY ? dims.y : minY;
    maxX = dims.x + dims.w > maxX ? dims.x + dims.w : maxX;
    maxY = dims.y + dims.h > maxY ? dims.y + dims.h : maxY;
  }
  if (labelBounds) {
    minX = labelBounds.x < minX ? labelBounds.x : minX;
    minY = labelBounds.y < minY ? labelBounds.y : minY;
    maxX = labelBounds.x + labelBounds.w > maxX ? labelBounds.x + labelBounds.w : maxX;
    maxY = labelBounds.y + labelBounds.h > maxY ? labelBounds.y + labelBounds.h : maxY;
  }
  return { x: minX, w: maxX - minX, y: minY, h: maxY - minY };
}

/**
 * Returns the label bounds
 */
export function getAllLabelBounds<
  K1,
  K2,
  D1 extends DiagramNodeData<K1>,
  D2 extends DiagramLinkData<K2, K1>
>(
  labelDims: Map<K1 | K2, TextDimensions>,
  nodes: D1[],
  links: D2[],
  nodeLabelPos: (DiagramLabelPosition | undefined)[] = [],
  linkLabelPos: DiagramLabelPosition[] = [],
  isRTL: boolean
) {
  if (nodeLabelPos.length === 0 && linkLabelPos.length === 0) return undefined;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let dims, label;
  for (let i = 0; i < nodeLabelPos.length; i++) {
    label = nodeLabelPos[i];
    if (!label) continue;
    dims = getLabelBounds(label, labelDims.get(nodes[i].id)!, isRTL);
    minX = dims.x < minX ? dims.x : minX;
    minY = dims.y < minY ? dims.y : minY;
    maxX = dims.x + dims.w > maxX ? dims.x + dims.w : maxX;
    maxY = dims.y + dims.h > maxY ? dims.y + dims.h : maxY;
  }
  for (let i = 0; i < linkLabelPos.length; i++) {
    label = linkLabelPos[i];
    if (!label) continue;
    dims = getLabelBounds(label, labelDims.get(links[i].id)!, isRTL);
    minX = dims.x < minX ? dims.x : minX;
    minY = dims.y < minY ? dims.y : minY;
    maxX = dims.x + dims.w > maxX ? dims.x + dims.w : maxX;
    maxY = dims.y + dims.h > maxY ? dims.y + dims.h : maxY;
  }
  return { x: minX, w: maxX - minX, y: minY, h: maxY - minY };
}

function getLabelBounds(label: DiagramLabelPosition, dims: TextDimensions, isRTL: boolean) {
  const labelPos = getLabelPosition(dims, label, isRTL);
  const isRotating = label.rotationAngle;
  if (!isRotating) {
    return { x: labelPos.x, y: labelPos.y, w: dims.width, h: dims.height };
  } else {
    // Find the 4 corners of the label bounds relative to the rotation point
    const rotationPoint = label.rotationPoint
      ? label.rotationPoint
      : { x: dims.width / 2, y: dims.height / 2 };
    const angle = label.rotationAngle!;
    const topLeft = getCoordsRelToRotPoint({ x: 0, y: 0 }, rotationPoint);
    const topRight = getCoordsRelToRotPoint({ x: dims.width, y: 0 }, rotationPoint);
    const bottomLeft = getCoordsRelToRotPoint({ x: 0, y: dims.height }, rotationPoint);
    const bottomRight = getCoordsRelToRotPoint({ x: dims.width, y: dims.height }, rotationPoint);
    const rotTopLeft = rotatePoint(topLeft, angle);
    const rotBotLeft = rotatePoint(bottomLeft, angle);
    const rotTopRight = rotatePoint(topRight, angle);
    const rotBotRight = rotatePoint(bottomRight, angle);
    const points = [rotTopLeft, rotBotLeft, rotTopRight, rotBotRight].map((pos: Position) => {
      return {
        x: pos.x + labelPos.x + rotationPoint.x,
        y: pos.y + labelPos.y + rotationPoint.y
      };
    });
    return getBoundsFromPoints(points);
  }
}

function getCoordsRelToRotPoint(labelPoint: Position, rotPoint: Position) {
  return { x: labelPoint.x - rotPoint.x, y: labelPoint.y - rotPoint.y };
}

function rotatePoint(pos: Position, angle: number) {
  return {
    x: pos.x * Math.cos(angle) - pos.y * Math.sin(angle),
    y: pos.x * Math.sin(angle) + pos.y * Math.cos(angle)
  };
}

function getBoundsFromPoints(points: Position[]) {
  let minX = points[0].x;
  let minY = points[0].y;
  let maxX = points[0].x;
  let maxY = points[0].y;
  for (let i = 1; i < points.length; i++) {
    const point = points[i];
    if (point.x < minX) minX = point.x;
    else if (point.x > maxX) maxX = point.x;
    if (point.y < minY) minY = point.y;
    else if (point.y > maxY) maxY = point.y;
  }
  return {
    x: minX,
    y: minY,
    w: maxX - minX,
    h: maxY - minY
  };
}

/**
 * Returns the position of the label
 * @param dimensions
 * @param positionProps
 * @returns position of the label
 */
export function getLabelPosition(
  dimensions: TextDimensions,
  positionProps: DiagramLabelPosition,
  isRTL: boolean
) {
  const position = positionProps.position;
  const hAlign = positionProps.hAlign ? positionProps.hAlign : isRTL ? 'right' : 'left';
  const vAlign = positionProps.vAlign;
  let x, y;
  if (hAlign === 'center') {
    x = position.x - dimensions.width / 2;
  } else if (hAlign === 'right') {
    x = position.x - dimensions.width;
  } else {
    x = position.x;
  }

  if (vAlign === 'middle') {
    y = position.y - dimensions.height / 2;
  } else if (vAlign === 'bottom') {
    y = position.y - dimensions.height;
  } else if (vAlign === 'baseline') {
    y = position.y + dimensions.y;
  } else {
    y = position.y;
  }
  return {
    x: x,
    y: y
  };
}

/**
 * Converts centerX,Y coordinates to panX,Y coordinates
 */
export function centerXYToPanXY(
  centerX: number,
  centerY: number,
  zoom: number,
  width: number,
  height: number
) {
  return {
    panX: width / 2 - centerX * zoom,
    panY: height / 2 - centerY * zoom
  };
}
/**
 * Converts panX,Y coordinates to centerX,Y coordinates
 */
export function panXYToCenterXY(
  panX: number,
  panY: number,
  zoom: number,
  width: number,
  height: number
) {
  return {
    centerX: (width / 2 - panX) / zoom,
    centerY: (height / 2 - panY) / zoom
  };
}

/**
 * Compares if two ItemInfo are equal
 */
export function isEqualItem<K1, K2>(item1?: ItemInfo<K1, K2>, item2?: ItemInfo<K1, K2>) {
  return item1?.id === item2?.id && item1?.isNode === item2?.isNode;
}

/**
 * Returns the item info of the data-oj-node or data-oj-link attribute for a given element.
 */
export function getNavigableInfo<K1, K2>(
  element: HTMLElement,
  rootElementRef?: Ref<HTMLDivElement>
): ItemInfo<K1, K2> {
  let isNode = false;
  let elem = element;
  let navigableId;
  while (!navigableId) {
    navigableId = elem.dataset['node'];
    if (navigableId) {
      isNode = true;
    } else {
      navigableId = elem.dataset['link'];
    }
    if (!navigableId) {
      if (elem.parentElement && elem.parentElement !== rootElementRef?.current) {
        elem = elem.parentElement;
      } else {
        break;
      }
    }
  }
  return {
    id: navigableId as K1 | K2,
    isNode: isNode
  };
}

/**
 * Returns the id of the focused item.
 */
export function getFocusedItem<K1, K2>(
  focusedItemInfo: ItemInfo<K1, K2>,
  hoveredItemInfo?: ItemInfo<K1, K2>
) {
  let focused;
  if (focusedItemInfo.isCurrent && focusedItemInfo.isFocusVisible) {
    focused = focusedItemInfo.id;
  } else if (hoveredItemInfo?.isCurrent) {
    focused = hoveredItemInfo.id;
  }
  return focused;
}

/**
 * Returns the aria label
 */
export function getItemAriaLabel(
  translations: BundleType,
  supportsSelection: boolean,
  isSelected?: boolean,
  accessibleLabel?: string
) {
  return generateAriaLabel(translations, accessibleLabel || '', {
    isSelected: !supportsSelection ? undefined : isSelected
  });
}

/**
 * Returns the zoom to fit zoom level
 */
export function getZoomToFitZoom(width: number, height: number, contentBounds: Bounds) {
  const zoomX = (width - 2 * ZOOM_TO_FIT_PADDING) / contentBounds.w;
  const zoomY = (height - 2 * ZOOM_TO_FIT_PADDING) / contentBounds.h;
  return Math.min(zoomX, zoomY);
}

/**
 * Constrains the zoom level
 */
function constrainZoom(zoom: number, minZoom: number, maxZoom: number) {
  let newZoom = Math.max(minZoom, zoom);
  newZoom = Math.min(maxZoom, newZoom);
  return newZoom;
}

/**
 * Returns the font props for a node or link
 */
function _getFontProps(styles?: DiagramLabelStyles) {
  const fontProps: Font = {};
  if (!styles) return fontProps;
  if (styles.fontFamily) fontProps.fontFamily = styles.fontFamily;
  if (styles.fontSize) fontProps.fontSize = styles.fontSize;
  if (styles.fontStyle) fontProps.fontStyle = styles.fontStyle;
  if (styles.fontWeight) fontProps.fontWeight = styles.fontWeight;
  return fontProps;
}

/**
 * Returns the panX, panY, and zoom when zooming and centering an item
 */
export function zoomAndCenterItemFromInfo<K1, K2, D2 extends DiagramLinkData<K2, K1>>(
  info: ItemInfo<K1, K2>,
  linkIdToDataMap: Map<K2, D2>,
  bounds: Bounds[],
  nodeIdToIndex: Map<K1, number>,
  width: number,
  height: number,
  maxZoom: number
) {
  const isNode = info.isNode;
  let totalBounds;
  if (isNode) {
    const nodeIndex = nodeIdToIndex.get(info.id as K1)!;
    totalBounds = bounds[nodeIndex];
  } else {
    const link = linkIdToDataMap.get(info.id as K2)!;
    const startNodeBounds = bounds[nodeIdToIndex.get(link.startNode)!];
    const endNodeBounds = bounds[nodeIdToIndex.get(link.endNode)!];
    const minX = Math.min(startNodeBounds.x, endNodeBounds.x);
    const minY = Math.min(startNodeBounds.y, endNodeBounds.y);
    const maxX = Math.max(
      startNodeBounds.x + startNodeBounds.w,
      endNodeBounds.x + startNodeBounds.w
    );
    const maxY = Math.max(startNodeBounds.y + startNodeBounds.h, endNodeBounds.y + endNodeBounds.h);
    totalBounds = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }
  return getPanZoomToCenterNode(totalBounds, maxZoom, width, height);
}

/**
 * Returns the pan and zoom values in order
 * to center a node or link
 */
function getPanZoomToCenterNode(bounds: Bounds, maxZoom: number, width: number, height: number) {
  const zoomX = (width - 2 * ZOOM_TO_FIT_PADDING) / bounds.w;
  const zoomY = (height - 2 * ZOOM_TO_FIT_PADDING) / bounds.h;
  const zoom = Math.min(zoomX, zoomY, maxZoom);
  const cX = bounds.w / 2 + bounds.x;
  const cY = bounds.h / 2 + bounds.y;
  return { centerX: cX, centerY: cY, zoom: zoom };
}
